import { useEffect } from 'react';
import './ThunderShockAnimation.css';

export default function ThunderShockAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get positions
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;

    // Track all created elements
    const elements = [];
    const timers = [];

    // Player charging animation
    playerEl.classList.add('thunder-charge');
    
    // Create charging particles around the player
    for (let i = 0; i < 6; i++) {
      const chargeTimer = setTimeout(() => {
        const chargeParticle = document.createElement('div');
        chargeParticle.className = 'thunder-charge-particle';
        
        // Position around the player with circular distribution
        const angle = (i / 6) * Math.PI * 2;
        const distance = playerRect.width * 0.5;
        chargeParticle.style.left = `${startX + Math.cos(angle) * distance}px`;
        chargeParticle.style.top = `${startY + Math.sin(angle) * distance}px`;
        
        battleBg.appendChild(chargeParticle);
        elements.push(chargeParticle);
        
        // Remove charge particle after animation
        const particleRemoveTimer = setTimeout(() => {
          if (chargeParticle.isConnected) {
            chargeParticle.remove();
          }
        }, 600);
        timers.push(particleRemoveTimer);
      }, i * 100);
      timers.push(chargeTimer);
    }
    
    // After charge, create the main lightning bolt
    const mainBoltTimer = setTimeout(() => {
      // Remove charging effect from player
      playerEl.classList.remove('thunder-charge');
      
      // Create lightning flash background
      const lightningFlash = document.createElement('div');
      lightningFlash.className = 'thunder-flash';
      battleBg.appendChild(lightningFlash);
      elements.push(lightningFlash);
      
      // Create main lightning bolt
      const bolt = document.createElement('div');
      bolt.className = 'thunder-bolt';
      
      // Position bolt at opponent
      bolt.style.left = `${endX}px`;
      bolt.style.top = `${endY - opponentRect.height * 0.6}px`; // Start from above
      bolt.style.height = `${opponentRect.height * 1.2}px`; // Make bolt taller than opponent
      
      battleBg.appendChild(bolt);
      elements.push(bolt);
      
      // Create secondary small bolts around the main one
      for (let i = 0; i < 5; i++) {
        const smallBoltTimer = setTimeout(() => {
          const smallBolt = document.createElement('div');
          smallBolt.className = 'thunder-small-bolt';
          
          // Random position around opponent
          const offsetX = (Math.random() - 0.5) * opponentRect.width * 1.5;
          const offsetY = (Math.random() - 0.5) * opponentRect.height * 0.5;
          
          smallBolt.style.left = `${endX + offsetX}px`;
          smallBolt.style.top = `${endY + offsetY}px`;
          smallBolt.style.height = `${30 + Math.random() * 50}px`;
          smallBolt.style.setProperty('--rotation', `${(Math.random() - 0.5) * 30}deg`);
          
          battleBg.appendChild(smallBolt);
          elements.push(smallBolt);
          
          // Remove small bolt after animation
          const smallBoltRemoveTimer = setTimeout(() => {
            if (smallBolt.isConnected) {
              smallBolt.remove();
            }
          }, 300);
          timers.push(smallBoltRemoveTimer);
        }, 100 + i * 80); // Staggered creation
        timers.push(smallBoltTimer);
      }
      
      // Opponent hit reaction
      const hitTimer = setTimeout(() => {
        // Make opponent flash and shake
        opponentEl.classList.add('thunder-hit');
        
        // Create sparks around the opponent
        for (let i = 0; i < 12; i++) {
          const spark = document.createElement('div');
          spark.className = 'thunder-spark';
          
          // Position around the opponent with slight randomness
          const angle = Math.random() * Math.PI * 2;
          const distance = 10 + Math.random() * 40;
          spark.style.left = `${endX + Math.cos(angle) * distance}px`;
          spark.style.top = `${endY + Math.sin(angle) * distance}px`;
          
          // Random size and rotation
          spark.style.setProperty('--size', `${5 + Math.random() * 8}px`);
          spark.style.setProperty('--rotation', `${Math.random() * 360}deg`);
          
          battleBg.appendChild(spark);
          elements.push(spark);
          
          // Remove spark after animation
          const sparkRemoveTimer = setTimeout(() => {
            if (spark.isConnected) {
              spark.remove();
            }
          }, 600 + Math.random() * 300);
          timers.push(sparkRemoveTimer);
        }
        
        // Reset opponent animation
        const opponentResetTimer = setTimeout(() => {
          if (opponentEl) {
            opponentEl.classList.remove('thunder-hit');
          }
        }, 500);
        timers.push(opponentResetTimer);
        
      }, 300);
      timers.push(hitTimer);
      
      // Remove lightning bolt after animation
      const boltRemoveTimer = setTimeout(() => {
        if (bolt.isConnected) {
          bolt.remove();
        }
        if (lightningFlash.isConnected) {
          lightningFlash.remove();
        }
      }, 800);
      timers.push(boltRemoveTimer);
      
    }, 600); // Time after charge before bolt appears
    timers.push(mainBoltTimer);
    
    // Clean up and notify parent after animation completes
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2000);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
      
      // Reset any modified styles/classes
      if (playerEl) {
        playerEl.classList.remove('thunder-charge');
      }
      if (opponentEl) {
        opponentEl.classList.remove('thunder-hit');
      }
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

ThunderShockAnimation.moveType = "electric";
